/**
 * Project API queries.
 */

import { apiClient } from "./client";

export interface Flow360ProjectLink {
  flow360_project_id: string;
  flow360_project_name?: string | null;
  node_count: number;
  case_count: number;
  last_synced_at?: string | null;
}

export interface ProjectActivitySummary {
  node_count: number;
  flow360_project_count: number;
  flow360_case_count: number;
  plot_count: number;
  artifact_count: number;
  data_node_count: number;
  data_size_bytes: number;
  spool_link_count: number;
  last_activity_at?: string | null;
  spool_last_activity_at?: string | null;
}

export interface Project {
  id: string;
  name: string;
  owner_id: string;
  description?: string | null;
  metadata?: Record<string, unknown> | null;
  member_role?: "owner" | "admin" | "editor" | "viewer" | string | null;
  membership_status?: "owner" | "accepted" | "pending" | "public" | string | null;
  member_accepted_at?: string | null;
  flow360_links?: Flow360ProjectLink[];
  activity_summary?: ProjectActivitySummary | null;
  created_at: string;
}

export function formatProjectActivityCount(value: number, singular: string, plural = `${singular}s`): string {
  return `${value.toLocaleString()} ${value === 1 ? singular : plural}`;
}

function formatProjectActivityBytes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  const digits = size >= 10 || unit === 0 ? 0 : 1;
  return `${size.toFixed(digits)} ${units[unit]}`;
}

export function projectActivityStats(project: Project): string[] {
  const summary = project.activity_summary;
  if (!summary) return [];
  const stats: string[] = [];
  if (summary.flow360_case_count > 0) stats.push(formatProjectActivityCount(summary.flow360_case_count, "Flow360 case"));
  if (summary.plot_count > 0) stats.push(formatProjectActivityCount(summary.plot_count, "plot"));
  if (summary.data_size_bytes > 0) stats.push(formatProjectActivityBytes(summary.data_size_bytes));
  if (summary.spool_link_count > 0) stats.push(formatProjectActivityCount(summary.spool_link_count, "Spool link"));
  if (stats.length === 0 && summary.node_count > 0) stats.push(formatProjectActivityCount(summary.node_count, "node"));
  return stats;
}

export const LOCAL_TSANGPO_PROJECT_ID = "local-tsangpo-demo";

const LOCAL_PROJECTS_STORAGE_KEY = "thread:local-projects:v1";

function localProjectsEnabled(): boolean {
  if (import.meta.env.MODE === "test") return false;
  if (import.meta.env.VITE_THREAD_ENV === "local") return true;
  if (typeof window === "undefined") return false;
  return window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
}

function preferLocalProjects(): boolean {
  return localProjectsEnabled() && import.meta.env.VITE_THREAD_LOCAL_PROJECTS === "true";
}

function makeLocalProject(id: string, name: string, createdAt = new Date().toISOString()): Project {
  return {
    id,
    name,
    owner_id: "local-user",
    description: "Local Thread V4 test project backed by the Tsangpo fixture.",
    metadata: {
      local_only: true,
      fixture: "tsangpo",
      branch: "tsangpo-fixture",
      preferred_view: "v4",
      source: "thread-local-demo",
    },
    created_at: createdAt,
  };
}

function defaultLocalProject(): Project {
  return makeLocalProject(LOCAL_TSANGPO_PROJECT_ID, "Tsangpo V4 local demo", "2026-05-16T00:00:00.000Z");
}

function readLocalProjects(): Project[] {
  if (!localProjectsEnabled() || typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(LOCAL_PROJECTS_STORAGE_KEY);
    if (!raw) return [defaultLocalProject()];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [defaultLocalProject()];
    const projects = parsed.filter((project): project is Project => (
      project &&
      typeof project.id === "string" &&
      typeof project.name === "string" &&
      typeof project.created_at === "string"
    ));
    return projects.length ? projects : [defaultLocalProject()];
  } catch {
    return [defaultLocalProject()];
  }
}

function writeLocalProjects(projects: Project[]): void {
  if (!localProjectsEnabled() || typeof window === "undefined") return;
  window.localStorage.setItem(LOCAL_PROJECTS_STORAGE_KEY, JSON.stringify(projects));
}

export function isLocalThreadProject(project: Pick<Project, "id" | "metadata">): boolean {
  return project.id.startsWith("local-") || project.metadata?.local_only === true;
}

export function isPendingProjectInvite(project: Pick<Project, "membership_status" | "member_accepted_at">): boolean {
  return project.membership_status === "pending" && !project.member_accepted_at;
}

export function localFixtureForProject(projectId: string | null | undefined): "tsangpo" | null {
  if (!projectId?.startsWith("local-")) return null;
  return "tsangpo";
}

export async function fetchProjects(): Promise<Project[]> {
  if (preferLocalProjects()) {
    const projects = readLocalProjects();
    writeLocalProjects(projects);
    return projects;
  }
  try {
    const projects = await apiClient<Project[]>("/projects");
    if (projects.length || !localProjectsEnabled()) return projects;
  } catch (error) {
    if (!localProjectsEnabled()) throw error;
  }
  const projects = readLocalProjects();
  writeLocalProjects(projects);
  return projects;
}

export async function fetchProject(id: string): Promise<Project> {
  if (id.startsWith("local-")) {
    const local = readLocalProjects().find((project) => project.id === id);
    if (local) return local;
  }
  return apiClient<Project>(`/projects/${id}`);
}

export async function resolveSlug(slug: string): Promise<string> {
  const res = await apiClient<{ id: string }>(`/projects/by-slug/${encodeURIComponent(slug)}`);
  return res.id;
}

export async function createProject(name: string): Promise<Project> {
  if (preferLocalProjects()) {
    const safeSuffix = Date.now().toString(36);
    const project = makeLocalProject(`local-project-${safeSuffix}`, name.trim() || "Untitled local project");
    const projects = [project, ...readLocalProjects()];
    writeLocalProjects(projects);
    return project;
  }
  try {
    return await apiClient<Project>("/projects", {
      method: "POST",
      body: { name },
    });
  } catch (error) {
    if (!localProjectsEnabled()) throw error;
    const safeSuffix = Date.now().toString(36);
    const project = makeLocalProject(`local-project-${safeSuffix}`, name.trim() || "Untitled local project");
    const projects = [project, ...readLocalProjects()];
    writeLocalProjects(projects);
    return project;
  }
}

export async function updateProject(
  id: string,
  updates: { name?: string; description?: string },
): Promise<Project> {
  if (id.startsWith("local-")) {
    const projects = readLocalProjects();
    const index = projects.findIndex((project) => project.id === id);
    if (index >= 0) {
      const updated = {
        ...projects[index]!,
        ...updates,
        metadata: projects[index]!.metadata,
      };
      projects[index] = updated;
      writeLocalProjects(projects);
      return updated;
    }
  }
  return apiClient<Project>(`/projects/${id}`, {
    method: "PATCH",
    body: updates,
  });
}

export async function deleteProject(id: string): Promise<void> {
  if (id.startsWith("local-")) {
    writeLocalProjects(readLocalProjects().filter((project) => project.id !== id));
    return;
  }
  await apiClient(`/projects/${id}`, { method: "DELETE" });
}

export interface ReimportResult {
  project_id: string;
  nodes_created: number;
  cases_imported: number;
  cases_skipped: number;
  folders_reimported?: number;
}

export async function reimportProject(id: string): Promise<ReimportResult> {
  return apiClient<ReimportResult>(`/projects/${id}/reimport`, {
    method: "POST",
  });
}

export async function deleteNode(nodeId: string): Promise<void> {
  await apiClient(`/nodes/${nodeId}`, { method: "DELETE" });
}

export interface ProjectOverrides {
  ref_area?: number | null;
  ref_area_units?: string | null;
}

/** @deprecated Use fetchReferenceQuantities instead */
export async function fetchOverrides(projectId: string): Promise<ProjectOverrides> {
  return apiClient<ProjectOverrides>(`/projects/${projectId}/overrides`);
}

/** @deprecated Use setReferenceQuantity instead */
export async function updateOverrides(
  projectId: string,
  overrides: ProjectOverrides,
): Promise<ProjectOverrides> {
  return apiClient<ProjectOverrides>(`/projects/${projectId}/overrides`, {
    method: "PATCH",
    body: overrides,
  });
}

// ---------------------------------------------------------------------------
// Reference quantities (first-class nodes)
// ---------------------------------------------------------------------------

export interface ReferenceQuantity {
  id: string;
  path: string;
  value_scalar?: number | null;
  value_json?: Record<string, unknown> | null;
  units?: string | null;
  source?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at?: string;
  updated_at?: string | null;
}

export interface SetReferenceQuantityResult {
  node_id: string;
  path: string;
  parameter: string;
  status: string;
}

export async function fetchReferenceQuantities(
  projectId: string,
  branch = "main",
): Promise<ReferenceQuantity[]> {
  // Backend #11187 changed response from bare list to {total, items}.
  // Accept both shapes for forward/backward compatibility.
  const raw = await apiClient<
    ReferenceQuantity[] | { total: number; items: ReferenceQuantity[] }
  >(
    `/projects/reference-quantities?project_id=${projectId}&branch=${encodeURIComponent(branch)}&limit=500`,
  );
  if (Array.isArray(raw)) return raw;
  return raw?.items ?? [];
}

export async function setReferenceQuantity(
  projectId: string,
  parameter: string,
  value: number | Record<string, unknown>,
  units?: string,
  source = "user_input",
  reason?: string,
  branch = "main",
): Promise<SetReferenceQuantityResult> {
  return apiClient<SetReferenceQuantityResult>("/projects/reference-quantities", {
    method: "POST",
    body: {
      project_id: projectId,
      parameter,
      value,
      units,
      source,
      reason,
      branch,
    },
  });
}

export async function deleteReferenceQuantity(
  parameter: string,
  projectId: string,
  branch = "main",
): Promise<{ parameter: string; status: string }> {
  return apiClient(`/projects/reference-quantities/${parameter}?project_id=${projectId}&branch=${encodeURIComponent(branch)}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Surface meshes (DAG nodes with kind = 'surface_mesh')
// ---------------------------------------------------------------------------

export interface ProjectSurfaceMesh {
  node_id: string;
  name: string;
  path: string;
  flow360_surface_mesh_id: string;
  flow360_geometry_id: string;
}

export async function fetchProjectSurfaceMeshes(
  projectId: string,
  branch = "main",
): Promise<ProjectSurfaceMesh[]> {
  return apiClient<ProjectSurfaceMesh[]>(
    `/projects/${projectId}/surface-meshes?branch=${encodeURIComponent(branch)}`,
  );
}
